// components/modals/CrewMemberModal.js
import React, { useState, useEffect } from 'react';
import { Button } from '../common';
import { useTranslations } from '../../hooks/useTranslations';
import { useServicesPG } from '../../hooks/useServicesPG';
import { WeeklyAvailability } from '../crew/WeeklyAvailability';

export function CrewMemberModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  crewMember = null, 
  mode = 'add'
}) {
  const t = useTranslations();
  // Color options for crew members
  const colorOptions = [
    { value: '#3B82F6', name: t.blue },
    { value: '#10B981', name: t.green },
    { value: '#F59E0B', name: t.yellow },
    { value: '#EF4444', name: t.red },
    { value: '#8B5CF6', name: t.purple },
    { value: '#06B6D4', name: t.cyan },
    { value: '#F97316', name: t.orange },
    { value: '#84CC16', name: t.lime },
    { value: '#EC4899', name: t.pink },
    { value: '#6366F1', name: t.indigo }
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    assigned_services: [],
    is_visible_to_customers: true,
    is_active: true,
    color: colorOptions[0].value,
    availability: {},
    work_frequency_weeks: 1,
    work_reference_date: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { services, loading: servicesLoading } = useServicesPG();

  // Reset form when modal opens/closes or crew member changes
  useEffect(() => {
    if (isOpen) {
      if (crewMember && mode === 'edit') {
        setFormData({
          name: crewMember.name || '',
          email: crewMember.email || '',
          phone: crewMember.phone || '',
          assigned_services: crewMember.assigned_services || [],
          is_visible_to_customers: crewMember.is_visible_to_customers !== undefined ? crewMember.is_visible_to_customers : true,
          is_active: crewMember.is_active !== undefined ? crewMember.is_active : true,
          color: crewMember.color || '',
          availability: crewMember.availability || {},
          work_frequency_weeks: crewMember.work_frequency_weeks || 1,
          work_reference_date: crewMember.work_reference_date || ''
        });
      } else {
        setFormData({
          name: '',
          email: '',
          phone: '',
          assigned_services: [],
          is_visible_to_customers: true,
          is_active: true,
          color: colorOptions[0].value,
          availability: {},
          work_frequency_weeks: 1,
          work_reference_date: ''
        });
      }
    }
  }, [isOpen, crewMember, mode]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleServiceToggle = (serviceId) => {
  setFormData(prev => {
    const currentServices = prev.assigned_services || [];
    const isAssigned = currentServices.includes(serviceId);
    
    return {
      ...prev,
      assigned_services: isAssigned
        ? currentServices.filter(id => id !== serviceId)
        : [...currentServices, serviceId]
    };
  });
};

const handleAvailabilityChange = (newAvailability) => {
  setFormData(prev => ({
    ...prev,
    availability: newAvailability
  }));
};

const isServiceAssigned = (serviceId) => {
  return formData.assigned_services?.includes(serviceId) || false;
};

  const validateForm = () => {
    const fieldErrors = {};
    
    if (!formData.name.trim()) {
      fieldErrors.name = t.nameRequired;
    } else if (formData.name.trim().length < 2) {
      fieldErrors.name = t.nameAtLeast2Characters;
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      fieldErrors.email = t.invalidEmailFormat;
    }
    
    if (!formData.assigned_services || formData.assigned_services.length === 0) {
      fieldErrors.services = t.atLeastOneServiceRequired;
    }

    if (formData.work_frequency_weeks > 1 && !formData.work_reference_date) {
      fieldErrors.work_reference_date = t.referenceDateRequired;
    }

    if (formData.work_frequency_weeks < 1 || formData.work_frequency_weeks > 8) {
      fieldErrors.work_frequency_weeks = t.workFrequencyInvalidRange;
    }

    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const crewMemberData = {
        ...formData,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim()
      };

      if (mode === 'edit') {
        await onSuccess(crewMember.id, crewMemberData, 'edit');
      } else {
        await onSuccess(crewMemberData, 'add');
      }
      
      setSubmitSuccess(true);
      
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle = mode === 'edit' ? t.editCrewMember : t.addNewCrewMember;
  const successMessage = mode === 'edit' ? t.crewMemberUpdated : t.crewMemberAdded;
  const successDescription = mode === 'edit' 
    ? t.crewMemberHasBeenUpdatedSuccessfully
    : t.newCrewMemberHasBeenAddedToTeam;
  const submitButtonText = mode === 'edit' 
    ? (isSubmitting ? t.updating : t.updateCrewMember)
    : (isSubmitting ? t.adding : t.addCrewMember);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{modalTitle}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {submitSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-4">
                <div className="flex">
                  <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="font-medium">{successMessage}</h4>
                    <p className="text-sm">{successDescription}</p>
                  </div>
                </div>
              </div>
            )}

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
                <p className="text-sm">{errors.submit}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.name} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
                  placeholder={t.enterFullName}
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.email}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
                  placeholder={t.enterEmailAddress}
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.phone}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
                  placeholder={t.enterPhoneNumber}
                />
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.color}
                </label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      className={`w-8 h-8 rounded-full border-2 ${
                        formData.color === color.value ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              {/* Customer Visibility */}
              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_visible_to_customers"
                    checked={formData.is_visible_to_customers}
                    onChange={handleInputChange}
                    className="h-4 w-4 ml-2 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">גלוי ללקוחות לקביעת תור עצמאית</span>
                </label>
              </div>

              {/* Active Status */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="h-4 w-4 ml-2 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">{t.memberIsActive}</span>
                </label>
              </div>

              {/* Assigned Services */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t.assignedServices} <span className="text-red-500">*</span>
                </label>
                
                {servicesLoading ? (
                  <div className="text-sm text-gray-500">{t.loadingServices}</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    {services.map(service => (
                      <label
                        key={service.id}
                        className="flex items-center space-x-3 p-3 bg-white rounded-md border border-gray-200 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isServiceAssigned(service.id)}
                          onChange={() => handleServiceToggle(service.id)}
                          className="h-4 w-4 ml-2 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {service.name}
                          </div>
                          {service.description && (
                            <div className="text-xs text-gray-500">
                              {service.description}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                
                {formData.assigned_services?.length === 0 && (
                  <p className="mt-1 text-sm text-red-600">{t.atLeastOneServiceRequired}</p>
                )}
              </div>

            </div>

            {/* Weekly Availability */}
            <div className="md:col-span-2">
              <WeeklyAvailability
                availability={formData.availability}
                onChange={handleAvailabilityChange}
                disabled={isSubmitting}
              />
            </div>

            {/* Work Frequency Section */}
            <div className="md:col-span-2">
              <div className="space-y-4">
                <div className="flex items-center space-x-2 text-blue-600 mb-4">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                  </svg>
                  <h3 className="text-lg font-medium">{t.workFrequency}</h3>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="space-y-4">
                    {/* Work Frequency Selector */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.worksEveryXWeeks}
                      </label>
                      <select
                        name="work_frequency_weeks"
                        value={formData.work_frequency_weeks}
                        onChange={handleInputChange}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={1}>{t.everyWeek}</option>
                        <option value={2}>{t.every2Weeks}</option>
                        <option value={3}>{t.every3Weeks}</option>
                        <option value={4}>{t.every4Weeks}</option>
                        <option value={5}>{t.every5Weeks}</option>
                        <option value={6}>{t.every6Weeks}</option>
                        <option value={7}>{t.every7Weeks}</option>
                        <option value={8}>{t.every8Weeks}</option>
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        {t.workFrequencyDescription}
                      </p>
                    </div>

                    {/* Reference Date Picker - Only show when frequency > 1 */}
                    {formData.work_frequency_weeks > 1 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.referenceDate}
                        </label>
                        <input
                          type="date"
                          name="work_reference_date"
                          value={formData.work_reference_date}
                          onChange={handleInputChange}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          {t.selectReferenceDate}
                        </p>
                        {errors.work_reference_date && (
                          <p className="text-red-500 text-sm mt-1">{errors.work_reference_date}</p>
                        )}
                      </div>
                    )}

                    {errors.work_frequency_weeks && (
                      <p className="text-red-500 text-sm">{errors.work_frequency_weeks}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                disabled={isSubmitting}
                className='ml-3'
              >
                {t.cancel}
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={isSubmitting || submitSuccess}
              >
                {submitButtonText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}